import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';

import { useNewCapability } from 'sharedHooks';
import { ToggleButton, TimeSelectorRow, GeneralButton } from 'sharedComponents';
import { LoadMeasurement } from './LoadMeasurement';
import { CollectRawData } from './CollectRawData';
import {
	opButtons,
	multiChartToggleOptions,
	multiChartOptions
} from '../constants';
import {
	adjustDatesForRegularTabs,
	adjustDatesForV2Tabs,
	formatMultiToggleOptions,
} from './helpers';
import useUserCapability from 'sharedHooks/useUserCapability';

import './style.scss';
import { NotificationTypeCode, apiCallStatus } from 'helpers/constants';
import { setLastWeekDates, check365Day } from '../../../../PowertrainAssetDetails/helpers';
import { dcuGeneration, lastCommandRequestType } from './constants';

const OperationalParametersHeaderComponent = ({
	model: {
		dateSelection,
		activeTab,
		multiChart,
		isCMDAsset,
		hasRawDataV2,
		selectedNotification,
		sensorGatewayInfo,
		downloadRawDataErrorData
	},
	actions: {
		downloadRawDataV2,
		setActiveTab,
		setSelectedDates,
		setActiveTrendTab,
		getSmartSensorLastCommand
	},
	toggleOptions,
}) => {
	const { t: translate } = useTranslation();
	const { showReportIssue } = useReportIssue();
	const newCapability = useNewCapability();
	const loadMeasurementOnDemandCapabilityAvailable = newCapability?.hasLoadMeasurementOnDemandCapability();
	const show365Days = ![opButtons.scatterPlot, opButtons.scatterPlotV2, opButtons.histogram, opButtons.histogramV2].includes(activeTab);
	const collectRawDataCapabilityAvailable = newCapability?.hasCollectRawDataCapability();
	const trendHistory365daysAvailable = newCapability?.hasTrendHistory365DaysAvailable() && show365Days ? 365 : 90;
	const [isLoadMeasurementDialogVisible, setIsLoadMeasurementDialogVisible] = useState(false);
	const [isCollectRawDataDialogVisible, setIsCollectRawDataDialogVisible] = useState(false);
	const hasTrendHistory365DaysAvailable = newCapability?.hasTrendHistory365DaysAvailable() && show365Days;
	const userCapabilities = useUserCapability();
	const DcuGenerationProperty = sensorGatewayInfo?.data?.dcuGeneration;

	useEffect(() => {
		toggleOptions.length && !toggleOptions.some(option => option.value === activeTab) && setActiveTab(toggleOptions[0].value);
	}, [toggleOptions]);


	const onToggleLoadMeasurementDialog = () => {
		if (DcuGenerationProperty !== dcuGeneration.DCUGENERATION_PROPERTY && !isLoadMeasurementDialogVisible) {
			getSmartSensorLastCommand(lastCommandRequestType.loadMeasurementOnDemand);
		}
		setIsLoadMeasurementDialogVisible(!isLoadMeasurementDialogVisible);
	};

	const onToggleCollectRawDataDialog = () => {
		if (DcuGenerationProperty !== dcuGeneration.DCUGENERATION_PROPERTY && !isCollectRawDataDialogVisible) {
			getSmartSensorLastCommand(lastCommandRequestType.collectRawDataOnDemand);
		}
		setIsCollectRawDataDialogVisible(!isCollectRawDataDialogVisible);
	};

	const handleSelectDate = (from, to) => {
		if (from && to) {
			setSelectedDates(from, to);
		}
	};

	useEffect(() => {
		return () => {
			// setLastWeekDates(); // COMMENTING AS PART OF BUG-428805
			setActiveTab(opButtons.trends);
			setActiveTrendTab(multiChartOptions.SINGLE);
		};
	}, []);

	const getAdjustedDateRange = (activeTab) => {
		const { from, to } = dateSelection;

		if (!from || !to) {
			handleSelectDate(from, to);
			return;
		}

		const isV2Tab = [opButtons.scatterPlotV2, opButtons.histogramV2].includes(activeTab);
		const adjustedDates = isV2Tab
			? adjustDatesForV2Tabs(from, to)
			: adjustDatesForRegularTabs(from, to);

		handleSelectDate(adjustedDates.from, adjustedDates.to);
	};

	const handleTabSelection = (tab) => {
		getAdjustedDateRange(tab);
		setActiveTab(tab);
		if (![opButtons.trends, opButtons.vibrationFFTV2].includes(tab) && tab && check365Day(dateSelection.from, dateSelection.to)) {
			setLastWeekDates(setSelectedDates, tab);
		}
	};

	const handleTrendTabSelection = (tab) => {
		setActiveTrendTab(tab);
	};

	// SET vibrationFFT TAB FROM NOTIFICATION
	useEffect(() => {
		if (selectedNotification) {
			const typeCode = selectedNotification?.notificationTypeCode;
			if (typeCode === NotificationTypeCode.LoadMeasurementProcessed || typeCode === NotificationTypeCode.LoadMeasurementRequestCompleted) {
				setActiveTab(opButtons.vibrationFFT);
			}
		}
	}, [selectedNotification]);

	if (downloadRawDataErrorData?.loadingStatus === apiCallStatus.ERROR) {
		return <ErrorView
			errorCode={downloadRawDataErrorData.errorCode}
			onRetry={() => downloadRawDataV2()}
			onReportIssue={showReportIssue}
		/>;
	}

	return <>
		<LoadMeasurement
			show={isLoadMeasurementDialogVisible}
			close={onToggleLoadMeasurementDialog}
			onRetry={() => getSmartSensorLastCommand(lastCommandRequestType.loadMeasurementOnDemand)}
		/>
		<CollectRawData
			show={isCollectRawDataDialogVisible}
			close={onToggleCollectRawDataDialog}
			onRetry={() => getSmartSensorLastCommand(lastCommandRequestType.collectRawDataOnDemand)}
		/>
		<Row className='tab-actions-container justify-content-end'>
			<Col xl lg={12}>
				<div className='toggle-buttons'>
					<ToggleButton
						options={toggleOptions}
						onSelected={handleTabSelection}
						selectedValue={activeTab}
					/>
					{userCapabilities.hasMultiGraphTabViewCapability() &&
						activeTab === opButtons.trends &&
						isCMDAsset &&
						<ToggleButton
							options={formatMultiToggleOptions(multiChartToggleOptions)}
							onSelected={handleTrendTabSelection}
							selectedValue={multiChart}
						/>
					}
				</div>
			</Col>
			{
				loadMeasurementOnDemandCapabilityAvailable &&
				<Col md='auto'>
					<GeneralButton
						type='normal'
						text={translate('ABB.Powertrain.Frontend.opLoadMeasurmentBtn')}
						onClick={onToggleLoadMeasurementDialog}
					/>
				</Col>
			}
			{
				[opButtons.vibrationFFT, opButtons.vibrationFFTV2].includes(activeTab) ?
					<>
						{
							collectRawDataCapabilityAvailable &&
							<Col md='auto'>
								<GeneralButton
									type='normal'
									text={translate('ABB.Powertrain.Frontend.opCollectRawDataBtn')}
									onClick={onToggleCollectRawDataDialog}
								/>
							</Col>
						}
						{
							hasRawDataV2 && activeTab === opButtons.vibrationFFTV2 &&
							<Col md='auto'>
								<GeneralButton
									type='normal'
									text={translate('ABB.Powertrain.Frontend.opDownloadRawDataBtn')}
									onClick={() => downloadRawDataV2()}
								/>
							</Col>
						}
					</> : null
			}
			{
				![opButtons.vibrationFFT, opButtons.vibrationFFTV2].includes(activeTab) &&
				<Col className='col-xl-auto col-lg col-sm-auto'>
					<TimeSelectorRow
						fromDate={dateSelection.from}
						toDate={dateSelection.to}
						label=''
						handleSelectDate={handleSelectDate}
						maxDaysRange={trendHistory365daysAvailable}
						maxDaysBack={trendHistory365daysAvailable}
						preSelectedDays={7}
						numberOfMonths={2}
						excludeToday={[opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab)}
						footerIntervalText={hasTrendHistory365DaysAvailable ? translate('ABB.Powertrain.Frontend.datePickerMax365DaysNote') : translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
					/>
				</Col>
			}
		</Row>
	</>;
};

OperationalParametersHeaderComponent.propTypes = {
	model: PropTypes.shape({
		dateSelection: PropTypes.shape({
			from: PropTypes.string,
			to: PropTypes.string
		}),
		hasRawData: PropTypes.bool,
		activeTab: PropTypes.string,
		multiChart: PropTypes.string,
		isCMDAsset: PropTypes.bool,
		hasRawDataV2: PropTypes.bool,
		sensorGatewayInfo: PropTypes.object,
		selectedNotification: PropTypes.shape({
			notificationTypeCode: PropTypes.string,
		}),
		downloadRawDataErrorData: PropTypes.shape({
			loadingStatus: PropTypes.string,
			errorCode: PropTypes.number,
		}),
	}),
	actions: PropTypes.shape({
		setActiveTab: PropTypes.func,
		setSelectedDates: PropTypes.func,
		setActiveTrendTab: PropTypes.func,
		getSmartSensorLastCommand: PropTypes.func,
		downloadRawDataV2: PropTypes.func,
	}),
	toggleOptions: PropTypes.array,
};

export default OperationalParametersHeaderComponent;
